import { useGetList, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { ATTACHMENTS_BUCKET } from "@/components/atomic-crm/providers/commons/attachments";

// Test rackets, loans and goods out without a sale (task 1.10b, D-040)

export const RACKET_STATUSES = [
  "in_house",
  "on_loan",
  "maintenance",
  "lost",
  "retired",
] as const;
export type RacketStatus = (typeof RACKET_STATUSES)[number];

export const LOAN_OUTCOMES = ["converted", "returned", "lost"] as const;
export type LoanOutcome = (typeof LOAN_OUTCOMES)[number];

export interface RacketPhoto {
  src: string;
  path: string;
  title: string;
  type?: string;
}

export interface RacketOverview {
  id: number;
  code: string;
  variant_id: number | null;
  model: string | null;
  status: RacketStatus;
  condition: string | null;
  holder_contact_id: number | null;
  holder_company_id: number | null;
  current_loan_id: number | null;
  purpose_id: number | null;
  lent_by: number | null;
  deal_id: number | null;
  expected_return_at: string | null;
  since: string | null;
  days_since: number | null;
  is_overdue: boolean;
}

export interface TestRacket {
  id: number;
  code: string;
  variant_id: number | null;
  model: string | null;
  status: RacketStatus;
  condition: string | null;
  notes: string | null;
  photo: RacketPhoto | null;
  holder_contact_id: number | null;
  holder_company_id: number | null;
  current_loan_id: number | null;
}

export interface Movement {
  id: number;
  racket_id: number;
  loan_id: number | null;
  kind: "delivery" | "return" | "transfer" | "status_change" | "correction";
  from_contact_id: number | null;
  from_company_id: number | null;
  to_contact_id: number | null;
  to_company_id: number | null;
  status_to: RacketStatus;
  note: string | null;
  moved_by: number | null;
  moved_at: string;
}

export interface Loan {
  id: number;
  contact_id: number | null;
  company_id: number | null;
  purpose_id: number;
  deal_id: number | null;
  lent_by: number | null;
  lent_at: string;
  expected_return_at: string | null;
  closed_at: string | null;
  outcome: LoanOutcome | null;
  outcome_at: string | null;
}

export interface CatalogRow {
  id: number;
  key: string;
  name: string;
  position: number;
  active: boolean;
}
export interface LoanPurpose extends CatalogRow {
  tracks_outcome: boolean;
}
export interface GoodsOutReason extends CatalogRow {
  needs_recipient: boolean;
}

export interface Variant {
  id: number;
  sku: string;
  color: string | null;
  size: string | null;
  product_id: number;
}

export interface LabelFormat {
  columns: number;
  rows: number;
  label_width_mm: number;
  label_height_mm: number;
  margin_top_mm: number;
  margin_left_mm: number;
}

export const DEFAULT_LABEL_FORMAT: LabelFormat = {
  columns: 3,
  rows: 8,
  label_width_mm: 70,
  label_height_mm: 37,
  margin_top_mm: 0,
  margin_left_mm: 0,
};

// Every query a racket command can change
export const RACKET_RESOURCES = [
  "test_rackets",
  "test_racket_overview",
  "test_racket_loans",
  "test_racket_movements",
  "goods_outs",
  "deals",
  "loan_conversion",
];

const all = { page: 1, perPage: 500 };

export const useLoanPurposes = () =>
  useGetList<LoanPurpose>("loan_purposes", {
    pagination: all,
    sort: { field: "position", order: "ASC" },
  });

export const useGoodsOutReasons = () =>
  useGetList<GoodsOutReason>("goods_out_reasons", {
    pagination: all,
    sort: { field: "position", order: "ASC" },
  });

export const useVariants = () =>
  useGetList<Variant>("product_variants", {
    pagination: all,
    sort: { field: "sku", order: "ASC" },
  });

export const useRacketOverview = (
  filter: Record<string, unknown> = {},
  enabled = true,
) =>
  useGetList<RacketOverview>(
    "test_racket_overview",
    { pagination: all, sort: { field: "code", order: "ASC" }, filter },
    { enabled },
  );

export const variantLabel = (variant?: Variant) =>
  variant
    ? [variant.sku, variant.color, variant.size].filter(Boolean).join(" · ")
    : "";

export const modelOf = (
  racket: Pick<RacketOverview, "model" | "variant_id">,
  variants: Variant[],
) =>
  racket.model ??
  variantLabel(variants.find((variant) => variant.id === racket.variant_id));

// The scanner types the code and Enter; codes are matched without case or
// surrounding spaces
export const normalizeCode = (value: string) => value.trim().toUpperCase();

export const findByCode = <T extends { code: string }>(
  rackets: T[],
  value: string,
) =>
  rackets.find((racket) => normalizeCode(racket.code) === normalizeCode(value));

// The racket of a code from the database, when the list on screen does not
// have it yet (a reader is faster than the first load)
export const fetchRacketByCode = async (
  value: string,
): Promise<RacketOverview | null> => {
  const codes = [...new Set([value.trim(), normalizeCode(value)])];
  const { data, error } = await getSupabaseClient()
    .from("test_racket_overview")
    .select("*")
    .in("code", codes)
    .limit(1);
  if (error) throw new Error(error.message);
  return (data?.[0] as RacketOverview | undefined) ?? null;
};

// Counts per model and status for the dashboard
export const dashboardCounts = (
  rackets: RacketOverview[],
  variants: Variant[],
) => {
  const byModel = new Map<string, Record<RacketStatus, number>>();
  for (const racket of rackets) {
    const model = modelOf(racket, variants) || "—";
    const counts =
      byModel.get(model) ??
      (Object.fromEntries(RACKET_STATUSES.map((s) => [s, 0])) as Record<
        RacketStatus,
        number
      >);
    byModel.set(model, {
      ...counts,
      [racket.status]: counts[racket.status] + 1,
    });
  }
  return [...byModel.entries()]
    .map(([model, counts]) => ({ model, counts }))
    .sort((a, b) => a.model.localeCompare(b.model));
};

export const parseLabelFormat = (value: unknown): LabelFormat => {
  if (!value || typeof value !== "object") return DEFAULT_LABEL_FORMAT;
  const raw = value as Record<string, unknown>;
  const pick = (key: keyof LabelFormat) =>
    typeof raw[key] === "number" && (raw[key] as number) >= 0
      ? (raw[key] as number)
      : DEFAULT_LABEL_FORMAT[key];
  return {
    columns: Math.max(1, pick("columns")),
    rows: Math.max(1, pick("rows")),
    label_width_mm: pick("label_width_mm"),
    label_height_mm: pick("label_height_mm"),
    margin_top_mm: pick("margin_top_mm"),
    margin_left_mm: pick("margin_left_mm"),
  };
};

export const callRacketFunction = async (
  name: string,
  args: Record<string, unknown>,
) => {
  const { data, error } = await getSupabaseClient().rpc(
    name as never,
    args as never,
  );
  if (error) throw new Error(error.message);
  return data as unknown;
};

// Runs a command, reports its error and refreshes what it changed; false
// when it failed, else what the function returned
export const useRacketCommand = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  return async (
    name: string,
    args: Record<string, unknown>,
    done?: string,
  ): Promise<false | { data: unknown }> => {
    let data: unknown;
    try {
      data = await callRacketFunction(name, args);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
      return false;
    }
    await Promise.all(
      RACKET_RESOURCES.map((resource) =>
        queryClient.invalidateQueries({ queryKey: [resource] }),
      ),
    );
    if (done) notify(done, { type: "info" });
    return { data };
  };
};

// One photo per racket, in the attachments bucket like the notes' files
export const uploadRacketPhoto = async (file: File): Promise<RacketPhoto> => {
  const ext = file.name.includes(".") ? `.${file.name.split(".").pop()}` : "";
  const path = `test-rackets/${crypto.randomUUID()}${ext}`;
  const storage = getSupabaseClient().storage.from(ATTACHMENTS_BUCKET);
  const { error } = await storage.upload(path, file);
  if (error) throw new Error(error.message);
  const { data } = storage.getPublicUrl(path);
  return { src: data.publicUrl, path, title: file.name, type: file.type };
};
